import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import {
  createApplication,
  deleteApplication,
  editApplication,
} from 'src/hooks/useApplications';
import { buildArn, getRoleName } from 'src/utils/generalUtils';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
} from '@cloudscape-design/components';
import { PermissionAwareButton } from 'src/components/PermissionAwareButton';

export const ApplicationModal = ({
  account,
  modalMode,
  values,
  onClose,
  visible,
  redirectLocation,
}: {
  account: string;
  modalMode: 'New' | 'Edit' | 'Delete';
  values?: {
    id?: string;
    name: string;
    description?: string;
    s3ScriptFileLocation: string;
    roleName?: string;
    roleArn: string;
    roleExternalId?: string;
    version?: number;
  };
  onClose: () => void;
  visible: boolean;
  redirectLocation?: string;
}) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const ApplicationValidationSchema = yup.object().shape({
    name: yup.string().required('Please specify an application name.'),

    s3ScriptFileLocation: yup
      .string()
      .required('Please supply the location of the userscript file.'),

    roleName: yup
      .string()
      .matches(
        /^[\w+=,.@-]+$/g,
        'The role name may only consist of letters, digits or the following special characters: _+=,.@-',
      )
      .min(1, 'The role name must be at least one character.')
      .max(64, 'The role name may not be longer than 64 characters.')
      .required(
        'Please supply the name of the MLPigeon IAM Access role to be used with this application.',
      ),
  });

  const initialValues = {
    name: values?.name || '',
    s3ScriptFileLocation: values?.s3ScriptFileLocation || '',
    description: values?.description || '',
    roleName: values?.roleArn ? getRoleName(values?.roleArn) : '',
    roleExternalId: values?.roleExternalId || '',
  };

  useEffect(() => {
    formik.setValues({
      name: values?.name || '',
      s3ScriptFileLocation: values?.s3ScriptFileLocation || '',
      description: values?.description || '',
      roleName: values?.roleArn ? getRoleName(values?.roleArn) : '',
      roleExternalId: values?.roleExternalId || '',
    });
    formik.setTouched({});
  }, [values]);

  const [error, setError] = useState<Error | null>(null);

  const formik = useFormik({
    initialValues,
    onSubmit: async (formValues) => {
      let response;
      // check the mode, call the correct api
      setLoading(true);
      const roleArn = buildArn(account, formValues.roleName);
      switch (modalMode) {
        case 'New':
          response = await createApplication(
            {
              application: Object.assign({ ...formValues }, { roleArn }),
              account: account,
            },
            queryClient,
          )
            .catch((e) => {
              setError(e);
              return e.message;
            })
            .finally(() => {
              setLoading(false);
            });
          break;
        case 'Edit':
          if (!values?.id) return;
          response = await editApplication(
            {
              id: values?.id,
              application: Object.assign({ ...formValues }, { roleArn }),
              account: account,
              version: (values?.version || 1) + 1,
            },
            queryClient,
          )
            .catch((e) => {
              setError(e);
              return e.message;
            })
            .finally(() => {
              setLoading(false);
            });
          break;
      }

      if (!response) onClose();
    },
    validationSchema: ApplicationValidationSchema,
  });

  const handleDeleteConfirm = async () => {
    if (!values?.id) return;
    setLoading(true);
    const response = await deleteApplication(
      {
        id: values?.id,
        account,
      },
      queryClient,
    )
      .catch((e) => {
        setError(e);
        return e.message;
      })
      .finally(() => {
        setLoading(false);
        if (redirectLocation) {
          navigate(redirectLocation);
        }
      });

    if (!response) onClose();
  };

  if (!visible) return <></>;

  return (
    <Modal
      visible={true}
      onDismiss={onClose}
      header={`${modalMode} Application`}
      footer={
        <Box float="right">
          <SpaceBetween size="m" direction="horizontal">
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            <PermissionAwareButton
              requiredPermission="ADMINISTER"
              actionDescription={`${modalMode === 'Delete' ? 'delete' : 'create or update'} applications`}
              loading={loading}
              variant={'primary'}
              onClick={() => {
                modalMode === 'Delete'
                  ? handleDeleteConfirm()
                  : formik.handleSubmit();
              }}
            >
              {modalMode === 'Delete' ? 'Delete Application' : 'Submit'}
            </PermissionAwareButton>
          </SpaceBetween>
        </Box>
      }
    >
      {modalMode === 'Delete' ? (
        <p>This will delete the Application. Confirm?</p>
      ) : (
        <div className="modal-form">
          <div>
            <form onSubmit={formik.handleSubmit}>
              <FormField
                errorText={formik.touched.name && formik.errors.name}
                label="Application Name"
              >
                <Input
                  value={formik.values.name}
                  placeholder="My MLPigeon Application"
                  onChange={(e) => formik.setFieldValue('name', e.detail.value)}
                  onBlur={() => {
                    formik.setTouched({ ...formik.touched, name: true });
                  }}
                />
              </FormField>
              <FormField
                errorText={formik.errors.description}
                label="Description"
              >
                <Input
                  value={formik.values.description}
                  placeholder="My MLPigeon application description"
                  onChange={(e) =>
                    formik.setFieldValue('description', e.detail.value)
                  }
                  onBlur={() => {
                    formik.setTouched({ ...formik.touched, description: true });
                  }}
                />
              </FormField>
              <FormField
                label="Userscript S3 Location"
                errorText={
                  formik.touched.s3ScriptFileLocation &&
                  formik.errors.s3ScriptFileLocation
                }
              >
                <Input
                  value={formik.values.s3ScriptFileLocation}
                  placeholder="s3://bucket_name/path/user_script.py"
                  onChange={(e) =>
                    formik.setFieldValue('s3ScriptFileLocation', e.detail.value)
                  }
                  onBlur={() => {
                    formik.setTouched({
                      ...formik.touched,
                      s3ScriptFileLocation: true,
                    });
                  }}
                />
              </FormField>
              <FormField
                label="Access IAM Role Name"
                errorText={formik.touched.roleName && formik.errors.roleName}
              >
                <Input
                  value={formik.values.roleName}
                  placeholder="MLPigeonAccessRole"
                  onChange={(e) =>
                    formik.setFieldValue('roleName', e.detail.value)
                  }
                  onBlur={() => {
                    formik.setTouched({ ...formik.touched, roleName: true });
                  }}
                />
              </FormField>
              <FormField
                label="Access IAM Role External Id"
                errorText={
                  formik.touched.roleExternalId && formik.errors.roleExternalId
                }
              >
                <Input
                  value={formik.values.roleExternalId}
                  placeholder="RoleId"
                  onChange={(e) =>
                    formik.setFieldValue('roleExternalId', e.detail.value)
                  }
                  onBlur={() => {
                    formik.setTouched({
                      ...formik.touched,
                      roleExternalId: true,
                    });
                  }}
                />
              </FormField>
            </form>
          </div>
        </div>
      )}
      {error && <p className="error-text">{error.message}</p>}
    </Modal>
  );
};
